import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Save } from 'lucide-react';
import formatApiError from '../../../utils/formatApiError';
import '../AIGarage.css';

/**
 * Retarget a registered repo at a different branch (or rename it).
 * Already-queued runs keep the branch they were queued with.
 */
const EditSecScanRepoModal = ({ isOpen, repo, onClose, onSubmit }) => {
  const [displayName, setDisplayName] = useState('');
  const [defaultRef, setDefaultRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !repo) return;
    setDisplayName(repo.displayName || '');
    setDefaultRef(repo.defaultRef || '');
    setError(null);
  }, [isOpen, repo]);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, submitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);
      await onSubmit({
        displayName: displayName.trim() || undefined,
        // Sent even when empty: that is how you reset to the default branch.
        defaultRef: defaultRef.trim(),
      });
      onClose();
    } catch (err) {
      setError(formatApiError(err, 'Could not update repository'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !repo) return null;

  const modalContent = (
    <div className="garage-modal-overlay" onClick={!submitting ? onClose : undefined}>
      <div className="garage-modal" onClick={(e) => e.stopPropagation()}>
        <div className="garage-modal-header">
          <h2>Edit repository</h2>
          <button className="garage-modal-close" onClick={onClose} disabled={submitting}>
            <X size={20} />
          </button>
        </div>

        <div className="garage-modal-body">
          <form onSubmit={handleSubmit}>
            <div className="garage-form-group">
              <label htmlFor="sec-scan-edit-name">Name</label>
              <input
                id="sec-scan-edit-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={submitting}
                autoFocus
              />
            </div>

            <div className="garage-form-group">
              <label htmlFor="sec-scan-edit-ref">
                Branch <span className="garage-optional">(Optional)</span>
              </label>
              <input
                id="sec-scan-edit-ref"
                value={defaultRef}
                onChange={(e) => setDefaultRef(e.target.value)}
                placeholder="Leave empty for the repository's default branch"
                aria-describedby="sec-scan-edit-ref-hint"
                disabled={submitting}
              />
              <p id="sec-scan-edit-ref-hint" className="garage-field-hint">
                New scans will use this branch. Scans already queued or running keep
                the branch they started with.
              </p>
            </div>

            <p className="sec-scan-muted">{repo.gitUrl}</p>

            {error && <div className="garage-error-message">{error}</div>}

            <div className="garage-modal-actions">
              <button
                type="button"
                className="garage-btn garage-btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button type="submit" className="garage-btn garage-btn-primary" disabled={submitting}>
                {submitting ? (
                  'Saving…'
                ) : (
                  <>
                    <Save size={16} style={{ marginRight: '6px' }} />
                    Save changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default EditSecScanRepoModal;
